import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, cn, useDragScroll } from '@evogard/ui';
import type { ManagementClaim, ManagementMap as MapData } from '@evogard/contract';
import {
  MANAGEMENT_DOTS_PER_PHASE,
  count,
  isClaimConclusion,
  managementSourcesOf,
  managementTopologyOf,
  type ClaimClosureKind,
  type ManagementLine,
  type ManagementStats,
  type ManagementTopology,
} from '@evogard/domain';
import { ClaimDot, ClaimPeek } from './ClaimDot.js';
import {
  LINE_LABEL,
  LINE_OF,
  LINE_TO,
  LINE_TONE,
  endLabel,
  situationLabel,
  stageLabel,
  stallLabel,
} from './managementLabels.js';
import type { ManagementOnly, ManagementPoint } from './useManagement.js';

type LineData = MapData['lines'][number];
type PhaseData = LineData['phases'][number];
type Detour = ManagementTopology['detours'][number] & { data: PhaseData };

/*
 * A geometria, em pixels: a faixa do nome da raia, o recuo do começo, a
 * largura de uma coluna (marcador, nome e entroncamento), o passo das
 * sub-paradas e o cabeçalho da raia.
 */
const R = { strip: 40, left: 96, col: 248, sub: 24, laneGap: 32, head: 56 };
const stationX = (i: number) => R.strip + R.left + i * R.col;
/** O entroncamento fica no vão antes da coluna seguinte, abaixo de nome nenhum. */
const forkX = (i: number) => stationX(i) + R.col - 34;
/** A largura do nome da etapa, acima da estrada. */
const LABEL_W = 196;

/** O que apaga um ponto: a busca que não o achou, ou o filtro do SLA. */
export interface Dimming {
  searching: boolean;
  slaOnly: boolean;
}

type OpenPoint = (point: ManagementPoint, title: string, only?: ManagementOnly) => void;
/** Abrir um fim — a lista dos encerrados nele. Sem a permissão de Encerrados, nulo. */
type OpenEnd = ((line: ManagementLine, kind: ClaimClosureKind) => void) | null;

const dimClaim = (c: ManagementClaim, d: Dimming) =>
  (d.searching && !c.matched) || (d.slaOnly && !c.slaExpired);
const dimStats = (s: ManagementStats, d: Dimming) =>
  (d.searching && s.matches === 0) || (d.slaOnly && s.slaExpired === 0);

/** Uma sub-parada: os recém-chegados, cada situação na ordem do catálogo e os parados. */
interface Sub {
  key: string;
  label: string;
  total: number;
  stall: boolean;
  /** O `step` do ponto que a lista abre; sem ele, a etapa inteira. */
  step?: string;
  only?: ManagementOnly;
}

function subsOf(line: ManagementLine, p: PhaseData): Sub[] {
  if (p.steps.length === 0 && p.station.onHold === 0) return [];
  const station = p.steps.length > 0 ? 'estacao' : undefined;
  return [
    {
      key: 'recem',
      label: 'Recém-chegados',
      total: p.station.total - p.station.onHold,
      stall: false,
      step: station,
    },
    ...p.steps.map((s) => ({
      key: s.situation,
      label: situationLabel(line, s.situation),
      total: s.total,
      stall: false,
      step: s.situation,
    })),
    ...(p.station.onHold > 0
      ? [
          {
            key: 'parados',
            label: 'Parados',
            total: p.station.onHold,
            stall: true,
            step: station,
            only: 'parado' as const,
          },
        ]
      : []),
  ];
}

/** Quanto a rua das sub-paradas desce abaixo da estrada. */
const subsDepth = (line: ManagementLine, p: PhaseData) => {
  const n = subsOf(line, p).length;
  return n ? 22 + n * R.sub : 0;
};

/*
 * Quanto o nome da etapa sobe acima da estrada: o nome que quebra, a linha dos
 * números, o "pode encerrar" com a quebra dele e as bolinhas. Contar errado é
 * o que cortava o rodapé da caixa do fluxograma; aqui o nome cresce para cima
 * e a raia reserva o espaço.
 */
function labelHeight(
  line: ManagementLine,
  p: PhaseData,
  ends: readonly ClaimClosureKind[],
): number {
  const name = stageLabel(line, p.phase).length > 24 ? 2 : 1;
  const endText = ends.length ? `pode encerrar: ${ends.map(endLabel).join(', ')}` : '';
  const endLines = Math.ceil(endText.length / 32);
  return 14 + 19 * name + 16 + 16 * endLines + (p.total > 0 ? 38 : 0);
}

/** Os conectores da aba: cada ramal que sairia da raia ganha uma letra, e a mesma letra na chegada. */
function connectorsOf(lines: LineData[], topos: Map<ManagementLine, ManagementTopology>) {
  const out = new Map<string, { letter: string; label: string }>();
  const into = new Map<string, { letter: string; label: string }[]>();
  let n = 0;
  for (const { line } of lines) {
    for (const stub of topos.get(line)!.stubs) {
      const to = stub.exit.department;
      if (!lines.some((l) => l.line === to)) continue;
      const letter = String.fromCharCode(65 + n++);
      out.set(`${line}:${stub.from}`, {
        letter,
        label: stubText(line, stub.exit.phase, to),
      });
      const key = `${to}:${stub.exit.arrivesAt}`;
      into.set(key, [
        ...(into.get(key) ?? []),
        { letter, label: `${stageLabel(line, stub.from)}, ${LINE_OF[line]}` },
      ]);
    }
  }
  return { out, into };
}
type Connectors = ReturnType<typeof connectorsOf>;

/** "Vai para a Indenização", ou "Acordo · vai para o Reparo" quando a etapa tem outro nome. */
function stubText(line: ManagementLine, phase: string, to: ManagementLine): string {
  const name = stageLabel(line, phase);
  const target = `vai para ${LINE_TO[to]}`;
  return name === LINE_LABEL[to]
    ? target[0]!.toUpperCase() + target.slice(1)
    : `${name} · ${target}`;
}

/**
 * Onde cada coisa da raia fica, sem desenhar: a avenida, os desvios, o
 * entroncamento e as alturas. A topologia é do domínio
 * (`managementTopologyOf`); aqui só se decide o lugar. A raia de cima precisa
 * disto da de baixo para saber onde emenda a saída.
 */
function geometryOf(data: LineData, topo: ManagementTopology, connectors: Connectors) {
  const line = data.line;
  const phaseOf = (code: string) => data.phases.find((p) => p.phase === code);
  const avenue = topo.avenue.flatMap((code) => phaseOf(code) ?? []);
  const detours: Detour[] = topo.detours.flatMap((d) => {
    const p = phaseOf(d.phase);
    return p && d.leavesFrom && avenue.some((a) => a.phase === d.leavesFrom)
      ? [{ ...d, data: p }]
      : [];
  });
  const col = (code: string) => avenue.findIndex((p) => p.phase === code);
  const endsOf = (code: string) => topo.ends[code] ?? [];
  /* O desvio sem volta desenha o desfecho como fim, e não no nome. */
  const detourEnds = (d: Detour) => (d.rejoinsAt ? endsOf(d.phase) : []);

  const contFrom = topo.continuation ? col(topo.continuationFrom[0] ?? '') : -1;
  const contAtEnd = topo.continuation !== null && contFrom === avenue.length - 1;
  const contMid = topo.continuation !== null && contFrom >= 0 && !contAtEnd;
  const avenueStubs = topo.stubs.filter(
    (s) => col(s.from) >= 0 && connectors.out.has(`${line}:${s.from}`),
  );
  const forks = new Set([
    ...detours.map((d) => d.leavesFrom!),
    ...avenueStubs.map((s) => s.from),
    ...(contMid ? [avenue[contFrom]!.phase] : []),
  ]);
  const arrivesOnAvenue = avenue.some((p) => connectors.into.has(`${line}:${p.phase}`));

  const above = Math.max(...avenue.map((p) => labelHeight(line, p, endsOf(p.phase))));
  const below = Math.max(
    ...avenue.map((p) => subsDepth(line, p)),
    arrivesOnAvenue ? 104 : 0,
    44,
  );
  const ya = R.head + above + 12;
  const hasLower = detours.length > 0 || avenueStubs.length > 0 || contMid;
  /* O corredor da volta tracejada: abaixo das sub-paradas da avenida, acima dos nomes dos desvios. */
  const ym = ya + below + 14;
  const aboveD = detours.length
    ? Math.max(...detours.map((d) => labelHeight(line, d.data, detourEnds(d))))
    : 0;
  const y2 = ym + 24 + aboveD;
  const belowD = Math.max(...detours.map((d) => subsDepth(line, d.data)), 64);
  const height = hasLower ? y2 + belowD + 28 : ya + below + 16;

  return {
    avenue,
    detours,
    col,
    endsOf,
    detourEnds,
    contFrom,
    contAtEnd,
    contMid,
    forks,
    ya,
    ym,
    y2,
    height,
  };
}

interface Path {
  d: string;
  kind: 'main' | 'side' | 'hand' | 'back' | 'sub';
  line: ManagementLine;
}

interface Drawn {
  nodes: ReactNode[];
  paths: Path[];
  width: number;
}

/**
 * Uma raia: a estrada da avenida com um marcador por etapa, as sub-paradas
 * descendo de cada marcador, o entroncamento onde a etapa se divide, os
 * desvios na fileira de baixo com a volta tracejada, o começo, o fim, a saída
 * que desce para a raia de baixo e os conectores.
 */
function drawLane(
  data: LineData,
  topo: ManagementTopology,
  top: number,
  next: { ya: number } | null,
  connectors: Connectors,
  render: (
    key: string,
    p: PhaseData,
    x: number,
    y: number,
    ends: readonly ClaimClosureKind[],
  ) => ReactNode,
  onOpenEnd: OpenEnd,
): Drawn {
  const line = data.line;
  const g = geometryOf(data, topo, connectors);
  const { avenue, detours, col, endsOf, forks } = g;
  const ya = top + g.ya;
  const ym = top + g.ym;
  const y2 = top + g.y2;
  const height = g.height;
  const tone = LINE_TONE[line];
  const nodes: ReactNode[] = [];
  const paths: Path[] = [];
  const go = (d: string, kind: Path['kind'] = 'main') => paths.push({ d, kind, line });
  const last = avenue.length - 1;
  let maxX = 0;
  const reach = (x: number) => (maxX = Math.max(maxX, x));

  /* Um evento do fluxograma: início, fim ou conector — a forma é a da casa. */
  const event = (
    key: string,
    cx: number,
    cy: number,
    kind: 'inicio' | 'fim' | 'conector',
    label: string,
    letter = '',
    onClick?: () => void,
  ) => {
    const Label = onClick ? 'button' : 'span';
    reach(cx + 60);
    nodes.push(
      <span
        key={`${key}-ev`}
        aria-hidden
        className={cn(
          'absolute z-20 grid place-items-center rounded-pilula text-xs font-bold text-texto',
          kind === 'inicio' &&
            'size-6 border-[2.5px] border-fase-verde bg-fase-verde-suave',
          kind === 'fim' &&
            'size-6 border-[3.5px] border-fase-vermelho bg-fase-vermelho-suave',
          kind === 'conector' &&
            'size-[30px] border-[1.5px] border-texto-suave bg-superficie outline-[1.5px] outline-offset-2 outline-texto-suave outline-solid',
        )}
        style={{
          left: cx - (kind === 'conector' ? 15 : 12),
          top: cy - (kind === 'conector' ? 15 : 12),
        }}
      >
        {letter}
      </span>,
      <Label
        key={`${key}-lbl`}
        {...(onClick ? { type: 'button' as const, onClick } : {})}
        className={cn(
          'absolute z-20 w-[104px] text-center text-xs leading-tight font-semibold text-texto-suave',
          onClick && 'rounded-pequeno hover:text-texto hover:underline',
        )}
        style={{ left: cx - 52, top: cy + (kind === 'conector' ? 20 : 17) }}
      >
        {label}
      </Label>,
    );
  };
  const diamond = (key: string, cx: number, label: string) =>
    nodes.push(
      <span
        key={`${key}-dia`}
        title={`${label}: aqui a etapa se divide`}
        className="absolute z-20 size-[18px] rotate-45 rounded-[3px] border-[1.5px] border-fase-amarelo bg-fase-amarelo-suave"
        style={{ left: cx - 9, top: ya - 9 }}
      />,
    );
  const sign = (key: string, x: number, y: number, text: ReactNode) =>
    nodes.push(
      <span
        key={`${key}-placa`}
        className="absolute z-20 -translate-y-1/2 rounded-pequeno border border-borda bg-superficie px-2 py-0.5 text-xs whitespace-nowrap text-texto-suave"
        style={{ left: x, top: y }}
      >
        {text}
      </span>,
    );
  const station = (
    p: PhaseData,
    x: number,
    y: number,
    ends: readonly ClaimClosureKind[],
  ) => {
    reach(x + LABEL_W + 20);
    nodes.push(render(p.phase, p, x, y, ends));
    const depth = subsDepth(line, p);
    if (depth) go(`M${x},${y + 10} L${x},${y + depth - R.sub + 2}`, 'sub');
  };
  /* A chegada por conector: abaixo e à esquerda do marcador, subindo até a estrada. */
  const arrivals = (code: string, x: number, y: number) =>
    (connectors.into.get(`${line}:${code}`) ?? []).forEach((c, k) => {
      const cx = x - 58 - k * 44;
      const cy = y + 62;
      event(`${code}-chega-${c.letter}`, cx, cy, 'conector', c.label, c.letter);
      go(
        `M${cx},${cy - 16} L${cx},${y + 12} Q${cx},${y} ${cx + 12},${y} L${x - 13},${y}`,
        'side',
      );
    });
  /*
   * A saída que desce para a raia de baixo: `start` leva o traço até `fromX`,
   * e dali ele desce pelo corredor entre as duas raias até o primeiro marcador
   * da de baixo — num traço só, para a seta ficar só na chegada.
   */
  const handOff = (start: string, fromX: number) => {
    if (!next) return go(start, 'hand');
    const cy = top + height + R.laneGap / 2;
    const cx = R.strip + 20;
    go(
      `${start} L${fromX},${cy - 12} Q${fromX},${cy} ${fromX - 12},${cy} L${cx + 12},${cy} Q${cx},${cy} ${cx},${cy + 12} L${cx},${next.ya - 12} Q${cx},${next.ya} ${cx + 12},${next.ya} L${stationX(0) - 13},${next.ya}`,
      'hand',
    );
  };
  const contText = topo.continuation && (
    <>
      <b className="font-semibold text-texto">
        {stageLabel(line, topo.continuation.phase)}
      </b>{' '}
      → {LINE_LABEL[topo.continuation.department]}
    </>
  );

  /* A raia: o nome na vertical, a cor do departamento e os números dele. */
  const sources = managementSourcesOf(line);
  nodes.push(
    <div
      key="raia"
      className="absolute inset-x-0 z-0 overflow-hidden rounded-borda border border-borda bg-superficie"
      style={{ top, height }}
    >
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', tone.fill)} />
      <span className="absolute inset-y-0 left-0 grid w-10 place-items-center border-r border-borda bg-superficie-suave">
        <span className="rotate-180 text-xs font-bold tracking-widest text-texto uppercase [writing-mode:vertical-rl]">
          {LINE_LABEL[line]}
        </span>
      </span>
    </div>,
    <LaneHead
      key="cabeca"
      data={data}
      sources={sources}
      style={{ left: R.strip + 44, top: top + 12 }}
      onOpenEnd={onOpenEnd}
    />,
  );

  /* O começo: o início do processo, quando ninguém manda para esta raia. */
  if (sources.length === 0) {
    const x0 = R.strip + 40;
    event(
      'inicio',
      x0,
      ya,
      'inicio',
      line === 'perifericos' ? 'Pedido do associado' : 'Aviso',
    );
    go(`M${x0 + 13},${ya} L${stationX(0) - 13},${ya}`);
  }

  const conclusion = (code: string) => endsOf(code).find(isClaimConclusion);
  /* A saída no fim da avenida desce depois de tudo da raia, para não cortar a fileira de baixo. */
  let tail: number | null = null;
  /* Onde a avenida termina: a placa da saída, ou o fim do processo. */
  const lastEndX = Math.max(
    forks.has(avenue[last]!.phase) ? forkX(last) + 64 : stationX(last) + 132,
    /* O rótulo do fim fica abaixo da estrada: com sub-paradas, só depois do nome delas. */
    subsDepth(line, avenue[last]!) ? stationX(last) + LABEL_W + 110 : 0,
  );
  avenue.forEach((p, i) => {
    const x = stationX(i);
    station(p, x, ya, endsOf(p.phase));
    arrivals(p.phase, x, ya);
    const isLast = i === last;
    const j = forkX(i);
    const fork = forks.has(p.phase);
    /* Para onde a estrada segue depois desta etapa: a próxima, a saída da raia ou o fim. */
    const endX = isLast ? lastEndX : null;
    const nextX = !isLast
      ? stationX(i + 1) - 13
      : g.contAtEnd
        ? endX!
        : conclusion(p.phase)
          ? endX! - 13
          : null;
    if (fork) {
      diamond(p.phase, j, stageLabel(line, p.phase));
      go(`M${x + 10},${ya} L${j - 13},${ya}`);
      if (nextX !== null) go(`M${j + 11},${ya} L${nextX},${ya}`);
    } else if (nextX !== null) go(`M${x + 10},${ya} L${nextX},${ya}`);

    if (isLast && g.contAtEnd) {
      sign('fim', endX! + 4, ya, contText);
      reach(endX! + 220);
      tail = endX!;
    } else if (isLast && conclusion(p.phase)) {
      event(
        'fim',
        endX!,
        ya,
        'fim',
        endLabel(conclusion(p.phase)!),
        '',
        onOpenEnd ? () => onOpenEnd(line, conclusion(p.phase)!) : undefined,
      );
    }

    /* O ramal que sai daqui para outra raia: desce do entroncamento até o conector. */
    const stub = connectors.out.get(`${line}:${p.phase}`);
    if (stub) {
      const sx = detours.some((d) => d.leavesFrom === p.phase) ? j + 150 : j;
      if (sx !== j)
        go(
          `M${j},${ya + 9} L${j},${ym - 12} Q${j},${ym} ${j + 12},${ym} L${sx - 12},${ym} Q${sx},${ym} ${sx},${ym + 12} L${sx},${y2 - 16}`,
          'side',
        );
      else go(`M${j},${ya + 9} L${j},${y2 - 16}`, 'side');
      event(`${p.phase}-sai`, sx, y2, 'conector', stub.label, stub.letter);
    }
    /* A saída da raia que nasce numa etapa do meio (Indenizados, na Aprovação Supervisor). */
    if (g.contMid && i === g.contFrom) {
      sign('meio', j + 12, ym + 12, contText);
      reach(j + 220);
      handOff(`M${j},${ya + 9}`, j);
    }
  });

  /* Os desvios: descem do entroncamento até a fileira de baixo, e a volta é tracejada. */
  const fromSame = new Map<string, number>();
  detours.forEach((d) => {
    const i = col(d.leavesFrom!);
    const k = fromSame.get(d.leavesFrom!) ?? 0;
    fromSame.set(d.leavesFrom!, k + 1);
    const j = forkX(i);
    const dx = j + 48 + k * (R.col + 40);
    go(
      `M${j},${ya + 9} L${j},${y2 - 12} Q${j},${y2} ${j + 12},${y2} L${dx - 13},${y2}`,
      'side',
    );
    station(d.data, dx, y2, g.detourEnds(d));
    arrivals(d.phase, dx, y2);
    let along = dx + LABEL_W + 30;
    if (d.rejoinsAt) {
      const toExit = d.rejoinsAt === topo.continuation?.phase && g.contAtEnd;
      const target = col(d.rejoinsAt);
      const tx = toExit ? lastEndX + 60 : stationX(target);
      const ty = toExit
        ? ya + 14
        : ya + Math.max(subsDepth(line, avenue[target]!) - R.sub + 2, 10) + 4;
      const ux = along;
      const dir = tx > ux ? 1 : -1;
      go(
        `M${dx + 10},${y2} L${ux - 12},${y2} Q${ux},${y2} ${ux},${y2 - 12} L${ux},${ym + 12} Q${ux},${ym} ${ux + 12 * dir},${ym} L${tx - 12 * dir},${ym} Q${tx},${ym} ${tx},${ym - 12} L${tx},${ty}`,
        'back',
      );
      nodes.push(
        <span
          key={`${d.phase}-volta`}
          className="absolute z-20 -translate-x-1/2 -translate-y-full bg-superficie px-1 text-[0.6875rem] font-bold tracking-wide text-texto-tenue uppercase"
          style={{ left: (ux + tx) / 2, top: ym - 2 }}
        >
          volta
        </span>,
      );
      along += 50;
    } else if (endsOf(d.phase)[0]) {
      const kind = endsOf(d.phase)[0]!;
      go(`M${dx + 10},${y2} L${along - 13},${y2}`, 'side');
      event(
        `${d.phase}-fim`,
        along,
        y2,
        'fim',
        endLabel(kind),
        '',
        onOpenEnd ? () => onOpenEnd(line, kind) : undefined,
      );
      along += 90;
    }
    const stub = connectors.out.get(`${line}:${d.phase}`);
    if (stub) {
      const sx = along;
      go(`M${dx + 10},${y2} L${sx - 16},${y2}`, 'side');
      event(`${d.phase}-sai`, sx, y2, 'conector', stub.label, stub.letter);
    }
  });

  if (tail !== null) {
    const hx = Math.max(tail + 190, maxX - 20);
    handOff(`M${tail},${ya} L${hx - 12},${ya} Q${hx},${ya} ${hx},${ya + 12}`, hx);
    reach(hx + 20);
  }

  return { nodes, paths, width: maxX + 40 };
}

/**
 * O Fluxo em linhas (26/10/2026): o fluxograma e o mapa de linhas numa visão
 * só. A topologia é a do fluxograma — as raias, o entroncamento onde a etapa
 * se divide, os desvios que descem e voltam tracejados, o início, o fim e os
 * conectores —; o desenho é o do mapa — a avenida é uma estrada na cor do
 * departamento, cada etapa é um marcador com as bolinhas dos acionamentos, e
 * as situações são uma rua curta que desce do marcador.
 *
 * As decisões do dono de 23/10 seguem valendo: o vermelho é o SLA da fase
 * expirado, o travado fica na estação, e não há link para a tela do
 * departamento — o clique abre a lista, e a bolinha abre a mesa.
 */
export function ManagementLines({
  lines,
  dimming,
  canOpen,
  onOpenClaim,
  onOpenPoint,
  onOpenEnd,
}: {
  lines: LineData[];
  dimming: Dimming;
  canOpen: (claim: ManagementClaim) => boolean;
  onOpenClaim: (claim: ManagementClaim) => void;
  onOpenPoint: OpenPoint;
  onOpenEnd: OpenEnd;
}) {
  const [peek, setPeek] = useState<{ claim: ManagementClaim; anchor: DOMRect } | null>(
    null,
  );

  const frame = useRef<HTMLDivElement>(null);
  useDragScroll(frame);
  const drawing = useMemo(() => {
    const topos = new Map(lines.map((l) => [l.line, managementTopologyOf(l.line)]));
    const connectors = connectorsOf(lines, topos);
    const geoms = lines.map((l) => geometryOf(l, topos.get(l.line)!, connectors));
    const tops: number[] = [];
    let top = 0;
    for (const g of geoms) {
      tops.push(top);
      top += g.height + R.laneGap;
    }
    const lanes = lines.map((l, i) =>
      drawLane(
        l,
        topos.get(l.line)!,
        tops[i]!,
        i < lines.length - 1 ? { ya: tops[i + 1]! + geoms[i + 1]!.ya } : null,
        connectors,
        (key, p, x, y, ends) => (
          <Station
            key={`${l.line}-${key}`}
            line={l.line}
            phase={p}
            x={x}
            y={y}
            ends={ends}
            dimming={dimming}
            canOpen={canOpen}
            onOpenClaim={onOpenClaim}
            onOpenPoint={onOpenPoint}
            onPeek={setPeek}
          />
        ),
        onOpenEnd,
      ),
    );
    return {
      lanes,
      width: Math.max(...lanes.map((l) => l.width), 640),
      height: Math.max(top - R.laneGap + 4, 0),
    };
  }, [lines, dimming, canOpen, onOpenClaim, onOpenPoint, onOpenEnd]);

  return (
    <div ref={frame} className="overflow-x-auto pb-2">
      <div className="relative" style={{ width: drawing.width, height: drawing.height }}>
        <svg
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 overflow-visible"
          width={drawing.width}
          height={drawing.height}
        >
          <defs>
            {lines.map((l) => (
              <Head
                key={l.line}
                id={`fluxo-seta-${l.line}`}
                className={LINE_TONE[l.line].head}
              />
            ))}
            <Head id="fluxo-seta-volta" className="fill-texto-tenue" />
          </defs>
          {/* A saída que emenda nas raias de baixo passa por cima das estradas delas. */}
          {(['sub', 'side', 'back', 'main', 'hand'] as const).map((kind) =>
            drawing.lanes.flatMap((lane, i) =>
              lane.paths
                .filter((p) => p.kind === kind)
                .map((p, k) => (
                  <path
                    key={`${kind}-${i}-${k}`}
                    d={p.d}
                    className={cn(
                      'fill-none',
                      kind === 'main' && cn('stroke-[4]', LINE_TONE[p.line].stroke),
                      kind === 'hand' && cn('stroke-[3]', LINE_TONE[p.line].stroke),
                      kind === 'side' &&
                        cn('stroke-[2.5] opacity-70', LINE_TONE[p.line].stroke),
                      kind === 'back' && 'stroke-texto-tenue stroke-[1.6]',
                      kind === 'sub' && 'stroke-borda-forte stroke-[1.5]',
                    )}
                    strokeDasharray={kind === 'back' ? '5 4' : undefined}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    markerEnd={
                      kind === 'sub'
                        ? undefined
                        : `url(#fluxo-seta-${kind === 'back' ? 'volta' : p.line})`
                    }
                  />
                )),
            ),
          )}
        </svg>
        {/* Cada raia na sua chave: as de dentro ("raia", "inicio") se repetem entre as raias. */}
        {drawing.lanes.map((lane, i) => (
          <Fragment key={lines[i]!.line}>{lane.nodes}</Fragment>
        ))}
      </div>
      {peek && <ClaimPeek {...peek} openable={canOpen(peek.claim)} />}
    </div>
  );
}

/** A ponta da seta, do tamanho fixo em pixel: não engorda com a espessura da estrada. */
function Head({ id, className }: { id: string; className: string }) {
  return (
    <marker
      id={id}
      viewBox="0 0 10 10"
      refX="8"
      refY="5"
      markerWidth="9"
      markerHeight="9"
      markerUnits="userSpaceOnUse"
      orient="auto-start-reverse"
    >
      <path d="M0,0 L10,5 L0,10 z" className={className} />
    </marker>
  );
}

/**
 * O cabeçalho da raia: quantos estão nela, quantos com o SLA expirado, de onde
 * ela recebe, o resumo dos travados por motivo e os fins com quantos chegaram
 * nos últimos 90 dias — o clique abre a lista de Encerrados.
 */
function LaneHead({
  data,
  sources,
  style,
  onOpenEnd,
}: {
  data: LineData;
  sources: ManagementLine[];
  style: { left: number; top: number };
  onOpenEnd: OpenEnd;
}) {
  return (
    <div
      className="absolute z-20 flex max-w-[56rem] flex-wrap items-center gap-x-3 gap-y-1 text-xs text-texto-suave"
      style={style}
    >
      <span className="font-semibold text-texto tabular-nums">
        {count(data.total, 'acionamento', 'acionamentos')}
      </span>
      {data.slaExpired > 0 && (
        <span className="font-semibold text-erro tabular-nums">
          {data.slaExpired} com SLA expirado
        </span>
      )}
      <span>
        {sources.length
          ? `Vem ${sources.map((s) => LINE_OF[s]).join(' e ')}`
          : data.line === 'perifericos'
            ? 'Entra pelo pedido do associado'
            : 'Entra pelo aviso'}
      </span>
      {data.stalls.length > 0 && (
        <span className="font-semibold text-alerta">
          Parados:{' '}
          {data.stalls.map((s) => `${stallLabel(data.line, s)} ${s.total}`).join(' · ')}
        </span>
      )}
      {data.closures.length > 0 && (
        <span className="flex flex-wrap items-center gap-1.5">
          <span>Encerrados (90 dias):</span>
          {data.closures.map((c) => {
            const Tag = onOpenEnd ? 'button' : 'span';
            return (
              <Tag
                key={c.kind}
                {...(onOpenEnd
                  ? {
                      type: 'button' as const,
                      onClick: () => onOpenEnd(data.line, c.kind),
                    }
                  : {})}
                className={cn(
                  'inline-flex items-center gap-1 rounded-pilula border border-fase-vermelho/50 bg-fase-vermelho-suave px-2 py-0.5 text-texto',
                  onOpenEnd && 'hover:underline',
                )}
              >
                <Icon
                  nome={isClaimConclusion(c.kind) ? 'confirmado' : 'bloqueado'}
                  size={16}
                />
                {endLabel(c.kind)}
                <span className="font-semibold tabular-nums">{c.total}</span>
              </Tag>
            );
          })}
        </span>
      )}
    </div>
  );
}

/**
 * Uma etapa: o marcador na estrada; acima dele o nome, os números, como pode
 * encerrar e as bolinhas (ou a pilha, acima de quatro); abaixo, a rua das
 * sub-paradas — os recém-chegados, cada situação e os parados.
 */
function Station({
  line,
  phase: p,
  x,
  y,
  ends,
  dimming,
  canOpen,
  onOpenClaim,
  onOpenPoint,
  onPeek,
}: {
  line: ManagementLine;
  phase: PhaseData;
  x: number;
  y: number;
  ends: readonly ClaimClosureKind[];
  dimming: Dimming;
  canOpen: (claim: ManagementClaim) => boolean;
  onOpenClaim: (claim: ManagementClaim) => void;
  onOpenPoint: OpenPoint;
  onPeek: (peek: { claim: ManagementClaim; anchor: DOMRect } | null) => void;
}) {
  const tone = LINE_TONE[line];
  const name = stageLabel(line, p.phase);
  const hits = dimming.searching ? (p.matches ?? 0) : null;
  const dim = dimStats(p, dimming);
  const items = [...p.station.items, ...p.steps.flatMap((s) => s.items)];
  const subs = subsOf(line, p);

  return (
    <>
      <div
        className={cn('absolute z-20 transition-opacity', dim && 'opacity-25')}
        style={{ left: x + 18, top: y - 10, width: LABEL_W }}
      >
        <div className="absolute bottom-0 left-0 flex w-full flex-col items-start gap-0.5">
          <button
            type="button"
            onClick={() => onOpenPoint({ line, phase: p.phase }, name)}
            className="rounded-pequeno text-left text-sm leading-tight font-bold text-texto hover:underline"
          >
            {name}
          </button>
          <span className="text-xs text-texto-suave tabular-nums">
            {p.total
              ? count(p.total, 'acionamento', 'acionamentos')
              : 'nenhum acionamento'}
            {p.slaExpired > 0 && (
              <span className="font-semibold text-erro">
                {' · '}
                {p.slaExpired === 1 ? '1 SLA expirado' : `${p.slaExpired} SLA expirados`}
              </span>
            )}
            {hits !== null && hits > 0 && (
              <span className="font-semibold text-texto">
                {' · '}
                {hits === 1 ? '1 achado' : `${hits} achados`}
              </span>
            )}
          </span>
          {ends.length > 0 && (
            <span className="text-xs leading-tight text-texto-tenue">
              pode encerrar: {ends.map(endLabel).join(', ')}
            </span>
          )}
          {p.total > 0 && (
            <span className="mt-1 flex items-center gap-1">
              {p.total > MANAGEMENT_DOTS_PER_PHASE ? (
                <Pile
                  stats={p}
                  searching={dimming.searching}
                  label={name}
                  onOpen={() => onOpenPoint({ line, phase: p.phase }, name)}
                />
              ) : (
                items.map((c) => (
                  <ClaimDot
                    key={`${c.source}-${c.id}`}
                    claim={c}
                    dim={dimClaim(c, dimming)}
                    onOpen={(claim) => canOpen(claim) && onOpenClaim(claim)}
                    onPeek={onPeek}
                  />
                ))
              )}
            </span>
          )}
        </div>
      </div>

      {/* O marcador de localização, com a ponta sobre a estrada; a busca o contorna. */}
      <svg
        aria-hidden
        width="26"
        height="34"
        viewBox="0 0 26 34"
        className={cn(
          'absolute z-20 drop-shadow-[0_1px_1px_rgb(0_0_0/0.3)] transition-opacity',
          dim && 'opacity-40',
        )}
        style={{ left: x - 13, top: y - 31 }}
      >
        <path
          d="M13 33C13 33 2 20.5 2 12.5A11 11 0 0 1 24 12.5C24 20.5 13 33 13 33Z"
          className={cn(
            tone.head,
            hits !== null && hits > 0
              ? 'stroke-texto stroke-[2.5]'
              : 'stroke-superficie stroke-[1.5]',
          )}
        />
        <circle cx="13" cy="12.5" r="4.6" className="fill-superficie" />
      </svg>

      {subs.map((s, k) => {
        const title = `${name} · ${s.key === 'recem' ? 'recém-chegados' : s.key === 'parados' ? 'parados' : s.label}`;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() =>
              onOpenPoint({ line, phase: p.phase, step: s.step }, title, s.only)
            }
            className={cn(
              'group absolute z-20 flex -translate-y-1/2 items-center gap-1.5 text-xs whitespace-nowrap',
              s.total === 0
                ? 'text-texto-tenue'
                : s.stall
                  ? 'font-semibold text-alerta'
                  : 'text-texto',
              dim && 'opacity-25',
            )}
            style={{ left: x - 5, top: y + 22 + k * R.sub }}
          >
            <span
              aria-hidden
              className={cn(
                'size-2.5 shrink-0 rounded-pilula border-2 bg-superficie',
                s.stall ? 'border-alerta' : 'border-borda-forte',
              )}
            />
            <span className="rounded-pequeno bg-superficie/85 px-0.5 group-hover:underline">
              {s.label}
            </span>
            <span
              className={cn(
                'min-w-5 rounded-pilula border border-borda bg-superficie-suave px-1.5 text-center tabular-nums',
                s.total === 0 && 'font-normal',
              )}
            >
              {s.total}
            </span>
          </button>
        );
      })}
    </>
  );
}

/**
 * Etapa cheia: acima de `MANAGEMENT_DOTS_PER_PHASE` as bolinhas viram a pilha —
 * o total, a barra que cresce devagar (em log, para 57 não esmagar as outras)
 * e a parte vermelha proporcional ao SLA expirado. O clique abre a lista.
 */
function Pile({
  stats,
  searching,
  label,
  onOpen,
}: {
  stats: ManagementStats;
  searching: boolean;
  label: string;
  onOpen: () => void;
}) {
  const width = Math.min(110, Math.round(26 + 15 * Math.log2(stats.total)));
  const expired = Math.round((stats.slaExpired / stats.total) * 100);
  const found = searching && (stats.matches ?? 0) > 0;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${label}: ${stats.total} acionamentos, ${stats.slaExpired} com SLA expirado. Ver todos`}
      className={cn(
        'flex items-center gap-2 rounded-borda px-1 py-0.5 transition-colors hover:bg-superficie-suave',
        found && 'bg-primaria-suave',
      )}
    >
      <span
        aria-hidden
        className="flex h-3 overflow-hidden rounded-pilula border-2 border-borda-forte bg-superficie-suave"
        style={{ width }}
      >
        <span className="h-full bg-erro" style={{ width: `${expired}%` }} />
      </span>
      <span
        className={cn(
          'text-xs font-semibold whitespace-nowrap tabular-nums',
          found ? 'text-texto' : stats.slaExpired > 0 ? 'text-erro' : 'text-texto-suave',
        )}
      >
        {found
          ? `${stats.matches} achado${stats.matches === 1 ? '' : 's'}`
          : stats.slaExpired > 0
            ? `${stats.slaExpired} expirado${stats.slaExpired === 1 ? '' : 's'}`
            : 'no prazo'}
      </span>
    </button>
  );
}
